/**
 * WHENEVER ATELIER — US Store State & Cart Engine (Shopify Cart API adapter)
 * Same public contract as mock store.js; backed by Shopify AJAX Cart endpoints.
 */

const WHENEVER_STORE = (function () {
  const CURRENCY = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.store && WHENEVER_DATA.store.currency) || "";
  const CURRENCY_SYMBOL = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.store && WHENEVER_DATA.store.currencySymbol) || "";
  const FREE_SHIPPING_THRESHOLD = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.store && WHENEVER_DATA.store.freeShippingThreshold) || 0;
  let currentCart = { items: [], item_count: 0, total_price: 0 };

  function sizedImage(src, width) {
    if (!src) return "";
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set("width", String(width));
      return u.href;
    } catch (e) {
      return src;
    }
  }

  function normalizeItems(cart) {
    return (cart.items || []).map(it => {
      const rawVariant = (it.variant_title || "").trim();
      const cleanVariant = rawVariant === "Default Title" ? "" : rawVariant.split("/").map(s => s.trim()).filter(Boolean).join(" / ");
      const parts = rawVariant.split("/").map(s => s.trim());
      const lineId = String(it.key || it.id || it.variant_id);
      const quantity = it.quantity || 1;
      const unitPrice = (it.final_price != null ? it.final_price : it.price) || 0;
      const originalPrice = (it.original_price != null ? it.original_price : it.price) || 0;
      const linePrice = it.final_line_price != null ? it.final_line_price : unitPrice * quantity;
      return {
        id: lineId,
        variantId: it.variant_id,
        productHandle: it.handle,
        title: it.product_title || it.title,
        variantTitle: cleanVariant,
        colorName: parts[0] || "",
        colorSlug: (parts[0] || "").toLowerCase().replace(/\s+/g, "-"),
        size: parts[1] || "",
        price: unitPrice / 100,
        originalPrice: originalPrice / 100,
        linePrice: linePrice / 100,
        image: sizedImage(it.image || (it.featured_image && it.featured_image.url) || "", 168),
        quantity,
        url: it.url || "",
      };
    });
  }

  function dispatchCartEvent(cart, pendingCount) {
    const items = normalizeItems(cart || currentCart);
    const detail = {
      items,
      count: cart ? cart.item_count : currentCart.item_count,
      subtotal: ((cart ? cart.total_price : currentCart.total_price) || 0) / 100,
      freeShippingRemaining: Math.max(0, FREE_SHIPPING_THRESHOLD - ((cart ? cart.total_price : currentCart.total_price) || 0) / 100),
      pending: typeof pendingCount === "number" ? pendingCount : cartQueue.length,
    };
    window.dispatchEvent(new CustomEvent("whenever:cart-updated", { detail }));
  }

  async function refresh() {
    try {
      const r = await fetch((window.themeConfig && themeConfig.routes.get_cart_url) || "/cart.js", { credentials: "same-origin" });
      if (!r.ok) throw new Error("cart");
      currentCart = await r.json();
    } catch (e) {
      return currentCart;
    }
    dispatchCartEvent(currentCart);
    return currentCart;
  }

  function getItems() {
    return normalizeItems(currentCart);
  }

  function getItemCount(items = null) {
    if (items) return items.reduce((t, i) => t + (parseInt(i.quantity, 10) || 1), 0);
    return currentCart.item_count || 0;
  }

  function getSubtotal(items = null) {
    if (items) return items.reduce((t, i) => t + (i.linePrice != null ? i.linePrice : (parseFloat(i.price) || 0) * (parseInt(i.quantity, 10) || 1)), 0);
    return (currentCart.total_price || 0) / 100;
  }

  function generateLineId(productHandle, colorSlug, size) {
    return `${productHandle}__${colorSlug}__${size}`.toLowerCase().replace(/[^a-z0-9_-]/g, "");
  }

  async function addItem({ productHandle, title, colorName, colorSlug, size, price, image, quantity = 1, variantId }) {
    if (!variantId && typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA) {
      const prod = (WHENEVER_DATA.products || []).find(p => p.handle === productHandle);
      const color = prod && prod.colors && (prod.colors.find(c => c.slug === colorSlug) || prod.colors[0]);
      const getVarId = c => c && (c.variantId || (c.variants && c.variants[0] && c.variants[0].variantId));
      variantId = getVarId(color) || (prod && prod.colors && getVarId(prod.colors[0]));
    }
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    let r;
    try {
      r = await fetch((window.themeConfig && themeConfig.routes.cart_add_url) || "/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });
    } catch (e) {
      return { success: false, error: "" };
    }
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      return { success: false, error: err.description || err.message || "" };
    }
    await refresh();
    return { success: true, items: getItems() };
  }

  const cartQueue = [];
  let isQueueProcessing = false;

  function findCartLine(lineId) {
    const idStr = String(lineId);
    return (currentCart.items || []).find(i => String(i.key) === idStr || String(i.id) === idStr || String(i.variant_id) === idStr);
  }

  async function processCartQueue() {
    if (isQueueProcessing) return;
    isQueueProcessing = true;
    try {
      while (cartQueue.length > 0) {
        const op = cartQueue.shift();
        let resolved = false;
        const resolveOp = () => {
          if (!resolved) {
            resolved = true;
            if (typeof op.resolve === "function") {
              try {
                op.resolve(currentCart);
              } catch (e) {
                // Ignore errors in caller's resolve callback
              }
            }
          }
        };

        try {
          const line = findCartLine(op.lineId);
          let targetQty = 0;
          if (op.type === "remove") {
            if (!line) {
              dispatchCartEvent(currentCart);
              resolveOp();
              continue;
            }
            targetQty = 0;
          } else if (op.type === "delta") {
            if (!line) {
              dispatchCartEvent(currentCart);
              resolveOp();
              continue;
            }
            targetQty = Math.max(0, (line.quantity || 0) + op.delta);
            if (targetQty === line.quantity) {
              dispatchCartEvent(currentCart);
              resolveOp();
              continue;
            }
          } else if (op.type === "set") {
            if (!line) {
              dispatchCartEvent(currentCart);
              resolveOp();
              continue;
            }
            targetQty = Math.max(0, op.quantity);
          }

          const targetId = (line && line.key) || op.lineId;
          let res = null;
          try {
            res = await fetch((window.themeConfig && themeConfig.routes.cart_change_url) || "/cart/change.js", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: targetId, quantity: targetQty }),
            });
          } catch (e) {
            res = null;
          }

          if (res && res.ok) {
            const freshCart = await res.json().catch(() => null);
            if (freshCart && Array.isArray(freshCart.items)) {
              currentCart = freshCart;
              dispatchCartEvent(currentCart);
            } else {
              await refresh();
            }
          } else {
            const body = res ? await res.json().catch(() => null) : null;
            const lineMissing = !!body && /not in cart|not found|cannot find/i.test(`${body.message || ""} ${body.description || ""}`);
            if (!lineMissing) {
              const i18n = (typeof WHENEVER_DATA !== "undefined" && WHENEVER_DATA.i18n) || {};
              const message = (body && body.description) || i18n.cartError || "";
              if (message && typeof window.showToast === "function") window.showToast(message);
            }
            await refresh();
          }
        } catch (opErr) {
          // Protect queue against listener exceptions or op errors
        } finally {
          resolveOp();
        }
      }
    } finally {
      isQueueProcessing = false;
    }
  }

  function enqueueCartOp(op) {
    return new Promise(resolve => {
      cartQueue.push({ ...op, resolve });
      processCartQueue();
    });
  }

  function adjustQuantity(lineId, delta) {
    return enqueueCartOp({ type: "delta", lineId, delta: parseInt(delta, 10) || 0 });
  }

  function updateQuantity(lineId, newQuantity) {
    return enqueueCartOp({ type: "set", lineId, quantity: Math.max(0, parseInt(newQuantity, 10) || 0) });
  }

  function removeItem(lineId) {
    return enqueueCartOp({ type: "remove", lineId });
  }

  function changeLine(lineId, quantity) {
    const qty = parseInt(quantity, 10) || 0;
    if (qty <= 0) return removeItem(lineId);
    return updateQuantity(lineId, qty);
  }

  async function clearCart() {
    await fetch("/cart/clear.js", { method: "POST", credentials: "same-origin" }).catch(() => null);
    await refresh();
  }

  function formatMoney(amount) {
    const val = parseFloat(amount) || 0;
    return `${CURRENCY_SYMBOL}${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${CURRENCY}`.trim();
  }

  function formatMoneyShort(amount) {
    const val = parseFloat(amount) || 0;
    return `${CURRENCY_SYMBOL}${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Boot: hydrate from server cart
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh);
  } else {
    refresh();
  }

  return {
    getItems,
    getItemCount,
    getSubtotal,
    generateLineId,
    addItem,
    updateQuantity,
    adjustQuantity,
    changeLine,
    removeItem,
    clearCart,
    refresh,
    formatMoney,
    formatMoneyShort,
    escapeHtml,
    sizedImage,
    FREE_SHIPPING_THRESHOLD,
  };
})();

if (typeof module !== "undefined" && module.exports) {
  module.exports = WHENEVER_STORE;
}

// sync-bump